import AutoSwipePanels from '../components/AutoSwipePanels';
import Footer from '../components/Footer.jsx';
import BoardFallback from '../components/BoardFallback.jsx';
import BoardErrorBoundary from '../components/BoardErrorBoundary.jsx';
import HiddenRestartTrigger from '../components/HiddenRestartTrigger.jsx';
import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../components/fids.css';

import Box from '@mui/material/Box';
import { useArrival } from '../context/Arrival.js';
import { getArrivalData } from '../Redux/Actions/Arrival/index.js';
import { apiConfigReady, getApiBaseUrl_dynamic, isCapacitor, buildAssetUrl, LAN_SERVER_URL } from '../config/config.js';
import { AirlineLogo, getAirlineLogoUrl } from '../utils/airlineLogos.js';
import { useAutoFont } from '../utils/useAutoFont.js';

import { getDisplayBaggage } from '../types/flight.js';
import { markRenderSuccess } from '../services/uiHealthMonitor.js';
import { updateFlightsDisplayedCount, updateFlightsReceivedCount } from '../services/flightDataMonitor.js';
import { useBoardSelfHealing } from '../hooks/useBoardSelfHealing.js';
import { useBoardHealthProbe } from '../hooks/useBoardHealthProbe.js';
import { useAllowedAirlines } from '../hooks/useAllowedAirlines.js';
import { serverDate } from '../services/serverClock.js';



const ROW_LINE_HEIGHT = 3.2;  // multiplier: rowHeight = cellFont × this

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
// Board clock follows the server clock, not the kiosk's drifting device clock
const hhmm = (d = serverDate()) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const timeFilter = (str) => {
  if (!str) return '';

  // Parse ISO string and convert to Lagos time (UTC+1)
  const utcDate = new Date(str);

  // Get UTC components
  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = utcDate.getUTCMonth();
  const utcDay = utcDate.getUTCDate();

  // Add 1 hour for Lagos timezone (UTC+1)
  const lagosDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, utcHours + 1, utcMinutes));

  const hh = pad2(lagosDate.getUTCHours());
  const mm = pad2(lagosDate.getUTCMinutes());

  return `${hh}:${mm}`;
};


const timeWithDayOffset = (isoString) => {
  if (!isoString) return '';

  // Parse as UTC and convert to Lagos time (UTC+1)
  const utcDate = new Date(isoString);

  // Get UTC components
  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = utcDate.getUTCMonth();
  const utcDay = utcDate.getUTCDate();

  // Add 1 hour for Lagos timezone (UTC+1)
  const lagosDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, utcHours + 1, utcMinutes));

  // Get today in Lagos timezone for day offset calculation
  const nowUtc = serverDate();
  const nowLagosDate = new Date(Date.UTC(
    nowUtc.getUTCFullYear(),
    nowUtc.getUTCMonth(),
    nowUtc.getUTCDate(),
    nowUtc.getUTCHours() + 1,
    nowUtc.getUTCMinutes()
  ));

  const today = new Date(nowLagosDate.getUTCFullYear(), nowLagosDate.getUTCMonth(), nowLagosDate.getUTCDate());
  const flightDay = new Date(lagosDate.getUTCFullYear(), lagosDate.getUTCMonth(), lagosDate.getUTCDate());

  const diffDays = Math.round((flightDay - today) / (1000 * 60 * 60 * 24));

  const hh = pad2(lagosDate.getUTCHours());
  const mm = pad2(lagosDate.getUTCMinutes());
  const baseTime = `${hh}:${mm}`;

  if (diffDays === 1) {
    return <span><span style={{ color: '#00FF00', fontWeight: 700 }}>+1</span> {baseTime}</span>;
  }

  if (diffDays === -1) {
    return <span><span style={{ color: '#FF3B3B', fontWeight: 700 }}>-1</span> {baseTime}</span>;
  }

  return baseTime;
};


const showActual = (status, t) => {
  if (!t) return false;
  const s = (status || '').toLowerCase();
  return !(s === 'scheduled' || s === 'in air' || s === 'in-air');
};

const chunkBy = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, (i + 1) * n));

const byTimeAsc = (a, b) => {
  const ta = new Date(a.estimatedTime || a.scheduledTime || 0).getTime();
  const tb = new Date(b.estimatedTime || b.scheduledTime || 0).getTime();
  return ta - tb; // Ascending: oldest first
};

const uniqueByKey = (list) => {
  const seen = new Map();
  for (const f of list || []) {
    if (!f?.key) continue;
    const prev = seen.get(f.key);
    if (!prev || (f.version ?? 0) >= (prev.version ?? 0)) seen.set(f.key, f);
  }
  return Array.from(seen.values()).sort(byTimeAsc);
};

const useWindowSize = () => {
  const [size, set] = useState({ w: window.innerWidth, h: window.innerHeight });
  useLayoutEffect(() => {
    const onR = () => set({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return size;
};

const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return;
    let rafId = 0;
    const ro = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        try {
          const cr = entry.contentRect;
          setSize((prev) =>
            prev.width === cr.width && prev.height === cr.height
              ? prev
              : { width: cr.width, height: cr.height }
          );
        } catch (_) { /* ignore */ }
      });
    });
    try { ro.observe(ref.current); } catch (_) { /* ignore */ }
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, []);
  return [ref, size];
};

const Arrival = () => {
  const dispatch = useDispatch();

  const [arrivalsRaw, , reconnectSocket] = useArrival();
  const arrivals = Array.isArray(arrivalsRaw) ? arrivalsRaw : [];

  // Ref for board container (used by health probe and error boundary)
  const boardContainerRef = useRef(null);

  const airportState = useSelector((s) => s.Airport?.Airport) || { set: false };
  const adConfig = useSelector((s) => s.Arrival?.Arrival) || [];

  const [now, setNow] = useState(hhmm());
  const [imageAds, setImageAds] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [adIndex, setAdIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [headerImagesLoaded, setHeaderImagesLoaded] = useState({});
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [paginationKey, setPaginationKey] = useState(0); // For resetting pagination

  // Track flights received from API (before filtering)
  useEffect(() => {
    updateFlightsReceivedCount(arrivals.length);
  }, [arrivals.length]);

  // Self-healing callbacks
  const handleRefetch = useCallback(() => {
    console.log('🔄 [Arrival] Self-healing triggered refetch');
    dispatch(getArrivalData());
  }, [dispatch]);

  const handleResetPagination = useCallback(() => {
    console.log('🔄 [Arrival] Self-healing reset pagination');
    setPaginationKey(k => k + 1);
  }, []);

  const win = useWindowSize();
  const H = win.h;  // screenHeight drives everything

  const headerHeight = Math.round(H * 0.102);       // 110px at 1080, 220px at 2160
  const [adRef, adSize] = useElementSize();
  const adColWidth = Math.round(win.w * 0.24);       // 24% of screen width (includes border)
  const adPadding = 0;

  const footerHeight = Math.round(H * 0.088);        // 95px at 1080, 190px at 2160
  const announcementHeight = footerHeight;
  const announceFont = Math.round(H * 0.041);        // 44px at 1080, 89px at 2160

  const bodyHeight = H - headerHeight - footerHeight; // 875px at 1080

  const cellFont = Math.round(H * 0.025);            // 27px at 1080, 54px at 2160
  const headFont = Math.round(H * 0.022);            // 24px at 1080, 48px at 2160
  const rowHeight = Math.round(cellFont * ROW_LINE_HEIGHT); // 86px at 1080, 173px at 2160
  const theadHeight = Math.round(rowHeight * 0.75);  // 65px at 1080, 130px at 2160
  const fallbackRows = rowHeight > 0 ? Math.floor((bodyHeight - theadHeight) / rowHeight) : 10;  // 9 data rows

  const headerTitleFont = Math.round(H * 0.052);     // 56px at 1080, 112px at 2160
  const headerClockFont = Math.round(H * 0.059);     // 64px at 1080, 127px at 2160
  const borderWidth = Math.max(3, Math.round(H * 0.003)); // 3px at 1080, 6px at 2160

  // Proportional padding/spacing (scales with screen height)
  const cellPad = Math.round(H * 0.011);         // 12px at 1080, 24px at 2160
  const cellPadSm = Math.round(H * 0.0074);      // 8px at 1080, 16px at 2160
  const cellPadXs = Math.round(H * 0.0037);      // 4px at 1080, 8px at 2160
  const headerPad = Math.round(H * 0.0093);      // 10px at 1080, 20px at 2160
  const btnPadV = Math.round(H * 0.0056);        // 6px at 1080, 12px at 2160
  const btnPadH = Math.round(H * 0.013);         // 14px at 1080, 28px at 2160
  const btnRadius = Math.round(H * 0.028);       // 30px at 1080, 60px at 2160
  const headerImgMax = Math.floor((headerHeight - borderWidth * 2) * 0.95); // fits inside border

  // Accessibility icon carousel (one at a time)
  const accessibilityImages = [
    `${process.env.PUBLIC_URL}/image/Accessibility-new.png`,
  ];

  const tableViewportRef = useRef(null);
  const tbodyRef = useRef(null);
  const theadRef = useRef(null);
  const [rowsPerPage, setRowsPerPage] = useState(fallbackRows);

  useLayoutEffect(() => {
    const vp = tableViewportRef.current;
    const thead = theadRef.current;
    if (!vp || !thead) return;

    const id = requestAnimationFrame(() => {
      const vpRect = vp.getBoundingClientRect();
      const theadRect = thead.getBoundingClientRect();
      const availableHeight = vpRect.height - theadRect.height;

      const calculatedRows = Math.max(6, Math.floor(availableHeight / rowHeight));

      if (calculatedRows !== rowsPerPage) setRowsPerPage(calculatedRows);
    });
    return () => cancelAnimationFrame(id);
  }, [win.w, win.h, rowHeight, cellFont, arrivals.length]);

  const fixedTableStyle = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };
  const adColStyle = {
    flex: `0 0 ${adColWidth}px`,
    maxWidth: adColWidth,
    minWidth: adColWidth,
    padding: 0,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: `${borderWidth}px solid white`,
    boxSizing: 'border-box',
    background: 'black',
  };
  const adFrameStyle = { position: 'relative', flex: 1, overflow: 'hidden', background: 'black', minHeight: 0, padding: Math.max(1, Math.round(H * 0.001)) };
  const mediaContain = { width: '100%', height: '100%', objectFit: 'fill', display: 'block' };

  const logoDim = Math.round(rowHeight * 1.0);
  const logoCellStyle = { width: logoDim, padding: `0 ${cellPadXs}px 0 ${cellPadSm}px`, textAlign: 'center', verticalAlign: 'middle', boxSizing: 'border-box', backgroundColor: 'white' };
  const logoSize = { height: `${Math.round(rowHeight * 0.95)}px`, width: logoDim };
  const airlineNameCellStyle = { textAlign: 'left', paddingLeft: cellPadSm, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: cellFont, verticalAlign: 'middle', boxSizing: 'border-box' };
  const destinationCellStyle = { padding: `${cellPadXs}px ${Math.round(H * 0.0056)}px`, textAlign: 'left', paddingLeft: cellPad, fontSize: cellFont, lineHeight: 1.35, boxSizing: 'border-box', wordWrap: 'break-word' };
  const rowStyle = { height: rowHeight, textAlign: 'center', fontSize: cellFont, boxSizing: 'border-box' };

  // Wait for API config detection to complete
  useEffect(() => {
    apiConfigReady.then(() => {
      setApiBaseUrl(getApiBaseUrl_dynamic());
    });
  }, []);

  useEffect(() => {
    dispatch(getArrivalData());
    const poll = setInterval(() => dispatch(getArrivalData()), 30000);
    return () => clearInterval(poll);
  }, [dispatch]);

  // Airport state is now initialized by ModeRedirector for all routes (including root '/')
  // No need to navigate away — ModeRedirector handles it before children render
  useEffect(() => { const id = setInterval(() => setNow(hhmm()), 1000); return () => clearInterval(id); }, []);

  useEffect(() => {
    console.log('🎬 Ads filter debug:', {
      adConfigLength: adConfig?.length,
      adConfigSample: adConfig?.[0],
      currentAirport: airportState?.airport,
      currentWing: airportState?.wing
    });

    if (!Array.isArray(adConfig) || !airportState?.airport) {
      console.log('❌ No ads - missing adConfig or airport state');
      setImageAds([]); setAllAds([]); return;
    }

    const scoped = adConfig.filter(
      (v) => v?.AirportInfo?.airport === airportState.airport && v?.AirportInfo?.wing === airportState?.wing
    );

    console.log('🎯 Filtered ads:', {
      totalAds: adConfig.length,
      filteredCount: scoped.length,
      filtered: scoped.map(v => ({ airport: v?.AirportInfo?.airport, wing: v?.AirportInfo?.wing, type: v?.type, file: v?.changeFileName }))
    });

    const imgs = scoped.filter((v) => v.type === 'image');
    const vids = scoped.filter((v) => v.type === 'video');

    console.log('📊 Final ads:', { images: imgs.length, videos: vids.length });

    setImageAds(imgs);
    setAllAds([...imgs, ...vids]);
    setAdIndex(0);
  }, [adConfig, airportState]);

  // Cycle through all ads in pairs every 8 seconds
  useEffect(() => {
    if (allAds.length <= 2) return;
    const timer = setInterval(() => {
      setAdIndex(prev => {
        const next = prev + 2;
        return next >= allAds.length ? 0 : next;
      });
    }, 15000);
    return () => clearInterval(timer);
  }, [allAds.length]);

  useEffect(() => {
    // Wait for API URL to be detected before loading images (fixes LAN mode race condition)
    // Note: apiBaseUrl can be empty string '' (valid for direct LAN access via NGINX proxy)
    // Only skip if apiBaseUrl is null/undefined (not yet initialized)
    if (!imageAds.length || apiBaseUrl === null || apiBaseUrl === undefined) return;

    console.log('🖼️ Loading ad images with apiBaseUrl:', apiBaseUrl || '(relative/empty)');

    const loadedState = {}; let loadedCount = 0;
    imageAds.forEach((ad) => {
      // Use buildAssetUrl for consistent URL construction
      const primaryUrl = buildAssetUrl(`/uploads/${ad.changeFileName}`);
      // Fallback to LAN URL if primary fails (helps when LAN detection fails but device is on LAN)
      const fallbackUrl = isCapacitor && apiBaseUrl !== LAN_SERVER_URL && apiBaseUrl !== ''
        ? `${LAN_SERVER_URL}/uploads/${ad.changeFileName}`
        : null;

      const img = new Image();

      // Set a timeout to prevent hanging on failed loads
      const timeout = setTimeout(() => {
        console.warn(`⏰ Image load timeout: ${ad.changeFileName}`);
        loadedState[ad.changeFileName] = false;
        if (++loadedCount === imageAds.length) setImagesLoaded({ ...loadedState });
      }, 15000); // 15 second timeout (increased to allow for fallback)

      const tryFallback = () => {
        if (fallbackUrl) {
          console.log(`🔄 Trying LAN fallback for: ${ad.changeFileName}`);
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            clearTimeout(timeout);
            console.log(`✅ Image loaded via LAN fallback: ${ad.changeFileName}`);
            loadedState[ad.changeFileName] = fallbackUrl; // Store the working URL
            if (++loadedCount === imageAds.length) setImagesLoaded({ ...loadedState });
          };
          fallbackImg.onerror = () => {
            clearTimeout(timeout);
            console.error(`❌ Image load failed (both primary and LAN fallback): ${ad.changeFileName}`);
            loadedState[ad.changeFileName] = false;
            if (++loadedCount === imageAds.length) setImagesLoaded({ ...loadedState });
          };
          fallbackImg.src = fallbackUrl;
        } else {
          clearTimeout(timeout);
          loadedState[ad.changeFileName] = false;
          if (++loadedCount === imageAds.length) setImagesLoaded({ ...loadedState });
        }
      };

      img.onload = () => {
        clearTimeout(timeout);
        console.log(`✅ Image loaded: ${ad.changeFileName}`);
        loadedState[ad.changeFileName] = true;
        if (++loadedCount === imageAds.length) setImagesLoaded({ ...loadedState });
      };
      img.onerror = (error) => {
        console.warn(`⚠️ Primary image load failed: ${ad.changeFileName}`, error);
        tryFallback();
      };
      img.src = primaryUrl;
    });
  }, [imageAds, apiBaseUrl]);

  // Preload header images
  useEffect(() => {
    const headerImages = [`${process.env.PUBLIC_URL}/image/landOn.png`, `${process.env.PUBLIC_URL}/image/FAAN_Logo.png`];
    const loadedState = {}; let loadedCount = 0;
    headerImages.forEach((src) => {
      const img = new Image();
      img.onload = () => { loadedState[src] = true; if (++loadedCount === headerImages.length) setHeaderImagesLoaded({ ...loadedState }); };
      img.onerror = () => { loadedState[src] = false; if (++loadedCount === headerImages.length) setHeaderImagesLoaded({ ...loadedState }); };
      img.src = src;
    });
  }, []);

  // Report successful render to UI health monitor
  // This runs after every render to track UI liveness
  useEffect(() => {
    markRenderSuccess('Arrival');
  });


  const allowedAirlines = useAllowedAirlines();

  console.log('🔍 ARRIVAL FILTER DEBUG:', {
    totalArrivals: arrivals.length,
    arrivalsSample: arrivals.slice(0, 3).map(f => ({
      flight: f.flight,
      airlineCode: f.airlineCode,
      airlineName: f.airlineName,
      key: f.key,
      status: f.status
    }))
  });

  const filteredArrivals = arrivals.filter(f => {
    const matchedByCode = f.airlineCode && allowedAirlines.includes(f.airlineCode);
    const matchedByName = f.airlineName && allowedAirlines.some(c => f.airlineName.toLowerCase().includes(c.toLowerCase()));
    const passed = matchedByCode || matchedByName;

    if (!passed) {
      console.log('❌ FILTERED OUT:', {
        flight: f.flight,
        airlineCode: f.airlineCode,
        airlineName: f.airlineName,
        reason: 'Not in allowed airlines'
      });
    }

    return passed;
  });

  console.log('✅ AFTER AIRLINE FILTER:', {
    filtered: filteredArrivals.length,
    flights: filteredArrivals.map(f => ({
      flight: f.flight,
      airlineCode: f.airlineCode,
      key: f.key
    }))
  });

  const deduped = uniqueByKey(filteredArrivals);

  // Track displayed count for self-healing (after filtering)
  useEffect(() => {
    updateFlightsDisplayedCount(deduped.length);
  }, [deduped.length]);

  // Board health probe — MutationObserver + periodic DOM check
  // Detects fallback-latched state from DOM (.board-fallback selector), no circular dependency
  const probeResult = useBoardHealthProbe({
    containerRef: boardContainerRef,
    currentFlightCount: deduped.length,
  });

  // Self-healing hook - monitors for blank screens and triggers recovery
  const {
    showFallback,
    fallbackReason,
    renderEpoch,
    lastKnownGood,
    recoveryLevel
  } = useBoardSelfHealing({
    boardType: 'arrivals',
    currentFlights: deduped,
    onRefetch: handleRefetch,
    onResetPagination: handleResetPagination,
    onReconnectSocket: reconnectSocket,
    probeResult,
  });

  // Callback for BoardErrorBoundary to trigger board remount
  const handleBoardErrorRecovery = useCallback(() => {
    console.log('🔄 [Arrival] BoardErrorBoundary triggered remount');
    setPaginationKey(k => k + 1);
    dispatch(getArrivalData());
  }, [dispatch]);

  console.log('✅ AFTER DEDUPLICATION:', {
    deduped: deduped.length,
    flights: deduped.map(f => ({
      flight: f.flight,
      airlineCode: f.airlineCode,
      key: f.key,
      version: f.version
    }))
  });

  const pageSize = rowsPerPage || fallbackRows;
  const pages = chunkBy(deduped, pageSize);
  const safePages = pages.length ? pages : [[]];

  console.log('✅ FINAL PAGES:', {
    pageCount: safePages.length,
    firstPageFlights: safePages[0]?.length || 0,
    totalFlightsAcrossPages: safePages.reduce((sum, page) => sum + page.length, 0)
  });

  // Column grid — proportional, all columns always visible (kiosk screens are always large)
  const responsiveColumns = {
    logoCol:    '6%',
    airlineCol: '18%',    // logo 6% + name 18% = 24% airline area
    flightCol:  '12%',
    partnerCol: '8%',
    etaCol:     '8%',
    ataCol:     '8%',
    originCol:  '14%',
    termCol:    '6%',
    bagCol:     '6%',
    statusCol:  '12%',
  };

  return (
    <div className="back" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', margin: 0, padding: 0 }}>
      <div className="fids_container" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', margin: 0, padding: 0 }}>
        {/* Header */}
        <div style={{ backgroundColor: '#FFD200', minHeight: headerHeight, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `${borderWidth}px solid black`, width: '100%', boxSizing: 'border-box', flexShrink: 0, padding: `0 ${headerPad}px`, overflow: 'visible' }}>
          {/* Left: icon + FAAN logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: headerPad, flexShrink: 0 }}>
            <div style={{ minWidth: headerImgMax, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {headerImagesLoaded[`${process.env.PUBLIC_URL}/image/landOn.png`] ? (
                <img src={`${process.env.PUBLIC_URL}/image/landOn.png`} alt="logo" style={{ maxHeight: headerImgMax, maxWidth: headerImgMax, objectFit: 'contain', display: 'block' }} />
              ) : (
                <div style={{ width: headerImgMax, height: headerImgMax }}></div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {headerImagesLoaded[`${process.env.PUBLIC_URL}/image/FAAN_Logo.png`] ? (
                <img src={`${process.env.PUBLIC_URL}/image/FAAN_Logo.png`} alt="logo" style={{ height: headerImgMax, objectFit: 'contain', display: 'block' }} />
              ) : (
                <div style={{ height: headerImgMax }}></div>
              )}
            </div>
          </div>
          {/* Center: title */}
          <div style={{ fontWeight: 700, fontSize: headerTitleFont, color: '#000000', textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1.1 }}>ARRIVAL</div>
          {/* Right: clock */}
          <div style={{ fontWeight: 700, fontSize: headerClockFont, color: '#000000', whiteSpace: 'nowrap', flexShrink: 0, padding: `0 ${headerPad}px`, lineHeight: 1.1 }}>{now}</div>
        </div>

        {/* Body */}
        <Box className="fids_box" sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0, margin: 0, padding: 0 }}>
          {/* Table side - shows fallback when board is blank, preserving ads and announcements */}
          <div ref={boardContainerRef} className="striped-container" style={{ flex: 1, minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            {showFallback ? (
              <BoardFallback
                boardType="arrivals"
                reason={fallbackReason}
                lastKnownGood={lastKnownGood}
                recoveryLevel={recoveryLevel}
              />
            ) : deduped.length === 0 ? (
              /* Explicit empty-state: no flights after filtering. Health probe treats this as healthy. */
              <div className="board-empty-state" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#888', fontSize: 28 }}>
                <div style={{ color: '#FFD200', fontSize: 32, marginBottom: 12 }}>No scheduled arrivals</div>
                <div>Flight information will appear when available</div>
              </div>
            ) : (
              <BoardErrorBoundary onRecovery={handleBoardErrorRecovery}>
              <AutoSwipePanels interval={15000} style={{ height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
              {safePages.map((page, i) => {
                const sliced = page.slice(0, pageSize);
                const padded = sliced.concat(Array.from({ length: Math.max(0, pageSize - sliced.length) }, () => null));
                return (
                  <div key={i} ref={i === 0 ? tableViewportRef : null} className="striped-container" style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#000000' }}>
                    <table style={{ ...fixedTableStyle, flex: '1 1 auto' }}>
                      <colgroup>
                        <col style={{ width: responsiveColumns.logoCol }} />
                        <col style={{ width: responsiveColumns.airlineCol }} />
                        <col style={{ width: responsiveColumns.flightCol }} />
                        <col style={{ width: responsiveColumns.partnerCol }} />
                        <col style={{ width: responsiveColumns.etaCol }} />
                        <col style={{ width: responsiveColumns.ataCol }} />
                        <col style={{ width: responsiveColumns.originCol }} />
                        <col style={{ width: responsiveColumns.termCol }} />
                        <col style={{ width: responsiveColumns.bagCol }} />
                        <col style={{ width: responsiveColumns.statusCol }} />
                      </colgroup>
                      <thead ref={i === 0 ? theadRef : null}>
                        <tr className="header" style={{ background: 'black', height: Math.round(rowHeight * 0.75), textAlign: 'center', fontSize: headFont, fontWeight: 700, color: 'white' }}>
                          <th colSpan={2} style={{ textAlign: 'left', paddingLeft: cellPad }}>AIRLINE</th>
                          <th style={{ textAlign: 'left', paddingLeft: cellPadSm }}>FLIGHT</th>
                          <th style={{ textAlign: 'left', paddingLeft: cellPadSm }}>PARTNER</th>
                          <th style={{ textAlign: 'right', paddingRight: cellPad }}>ETA</th>
                          <th style={{ textAlign: 'right', paddingRight: cellPad }}>ATA</th>
                          <th style={{ textAlign: 'left', paddingLeft: cellPad }}>ORIGIN</th>
                          <th style={{ padding: `0 ${cellPadXs}px` }}>TERM</th>
                          <th style={{ padding: `0 ${cellPadXs}px` }}>BAG</th>
                          <th style={{ padding: `0 ${cellPadXs}px` }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody ref={tbodyRef}>
                        {padded.map((info, idx) => {
                          // Calculate global row index for consistent striping across data and ghost rows
                          const globalRowIndex = (i * pageSize) + idx;
                          const isEvenRow = globalRowIndex % 2 === 0;

                          // Define row-specific style with explicit background color
                          const currentRowStyle = {
                            ...rowStyle,
                            backgroundColor: isEvenRow ? '#1a1a1a' : '#000000'
                          };

                          // Check if logo exists for this airline
                          const hasLogo = info && info.airlineCode && getAirlineLogoUrl(info.airlineCode);

                          return (
                          <tr
                            key={idx}
                            style={currentRowStyle}
                            className={info ? (isEvenRow ? 'data-row-even' : 'data-row-odd') : (isEvenRow ? 'ghost-row-even' : 'ghost-row-odd')}
                          >
                            {info ? (
                              <>
                                <td
                                  className={`airline-cell ${
                                    hasLogo
                                      ? "has-logo"
                                      : isEvenRow
                                      ? "no-logo even"
                                      : "no-logo odd"
                                  }`}
                                  style={{ ...logoCellStyle, backgroundColor: hasLogo ? 'white' : (isEvenRow ? '#1a1a1a' : '#000000') }}
                                >
                                  <AirlineLogo airlineCode={info.airlineCode} height={logoSize.height} width={logoSize.width} />
                                </td>
                                <td style={airlineNameCellStyle}>{info.airlineName}</td>
                                <td style={{ textAlign: 'left', paddingLeft: cellPadSm, boxSizing: 'border-box' }}>{info.flight}</td>
                                <td style={{ textAlign: 'left', padding: `0 ${cellPadXs}px`, boxSizing: 'border-box' }}>
                                  {Array.isArray(info.codeSharesAsCodes) && info.codeSharesAsCodes.length ? (
                                    info.codeSharesAsCodes.slice(0, 3).map((c, ii) => (
                                      <div key={ii} style={{ lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c}>{c}</div>
                                    ))
                                  ) : (<div>—</div>)}
                                </td>
                                <td style={{ textAlign: 'right', paddingRight: cellPad, boxSizing: 'border-box' }}>{timeWithDayOffset(info.estimatedTime || info.scheduledTime)}</td>
                                <td style={{ textAlign: 'right', paddingRight: cellPad, color: '#FFD200', boxSizing: 'border-box' }}>{showActual(info.status, info.actualTime) ? timeFilter(info.actualTime) : '—'}</td>
                                <td style={destinationCellStyle}>{info.displayname}</td>
                                <td style={{ textAlign: 'center', padding: `0 ${cellPadXs}px`, boxSizing: 'border-box' }}>T2</td>
                                <td style={{ textAlign: 'center', padding: `0 ${cellPadXs}px`, boxSizing: 'border-box' }}>{getDisplayBaggage(info) || '—'}</td>
                                <td style={{ textAlign: 'center', padding: `0 ${cellPadXs}px`, boxSizing: 'border-box' }}>
                                  <button style={{ fontSize: cellFont, whiteSpace: 'nowrap', padding: `${btnPadV}px ${btnPadH}px`, borderRadius: btnRadius }} className={`${(typeof info.status === 'string' ? info.status : '').replace(/[^A-Za-z0-9]/g, ' ').trim().replace(/\s+/g,' ')} button`}>
                                    {info.status === 'Landed' ? 'Arrived' : (info.status || '—')}
                                  </button>
                                </td>
                              </>
                            ) : (
                              // Empty rows with visible background striping at reduced opacity
                              <>
                                {Array.from({ length: 10 }).map((_, j) => (
                                  <td
                                    key={j}
                                    className={j === 0 ? `airline-cell no-logo ${isEvenRow ? 'even' : 'odd'}` : ''}
                                    style={{
                                      width: j === 0 ? logoDim : undefined,
                                      height: rowHeight,
                                      padding: 0,
                                      backgroundColor: j === 0 ? (isEvenRow ? '#1a1a1a' : '#000000') : undefined
                                    }}
                                  >
                                    <div style={{ height: '100%', width: '100%' }}></div>
                                  </td>
                                ))}
                              </>
                            )}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </AutoSwipePanels>
              </BoardErrorBoundary>
            )}
          </div>

          {/* Ad column */}
          <div ref={adRef} style={adColStyle}>
            {[adIndex, adIndex + 1].map((idx) => {
              const ad = allAds[idx];
              return (
                <div key={idx} style={adFrameStyle}>
                  {!ad ? (
                    <div style={{ color: '#999', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>Ad Space Available</div>
                  ) : ad.type === 'video' ? (
                    <video autoPlay muted loop style={mediaContain}>
                      <source src={buildAssetUrl(`/uploads/${ad.changeFileName}`)} type="video/mp4" />
                    </video>
                  ) : imagesLoaded[ad.changeFileName] ? (
                    <img
                      style={mediaContain}
                      src={typeof imagesLoaded[ad.changeFileName] === 'string'
                        ? imagesLoaded[ad.changeFileName]
                        : buildAssetUrl(`/uploads/${ad.changeFileName}`)}
                      alt={ad.companyName}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="color:#666;height:100%;display:flex;align-items:center;justify-content:center;background:#111">Ad temporarily unavailable</div>';
                      }}
                    />
                  ) : (
                    <div style={{ color: '#666', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
                  )}
                </div>
              );
            })}
          </div>
        </Box>

        {/* Footer */}
        <div style={{
          height: announcementHeight,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          flexShrink: 0,
          margin: 0,
          padding: 0
        }}>
          <Footer
            announcementHeight={announcementHeight}
            announceFont={announceFont}
            accessibilityImages={accessibilityImages}
            accessibilityWidth={adColWidth}
            wing="arrival"
          />
        </div>
      </div>
      {/* Hidden restart trigger - long press bottom-right corner to restart */}
      <HiddenRestartTrigger />
    </div>
  );
};

export default Arrival;